import { Injectable, Injector, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ManagementService } from '@principle-theorem/ng-principle-shared';
import {
  Firestore,
  isWithRef,
  shareReplayCold,
} from '@principle-theorem/shared';
import { type Observable, combineLatest, from, of } from 'rxjs';
import { filter, map, startWith, switchMap } from 'rxjs/operators';
import { ManagementToolRegistryService } from './management-tool-registry.service';
import { ManagementToolSidebarService } from './management-tool-sidebar.service';
import { type IManagementResource } from './models/management-resource';
import {
  type IResourceContext,
  type IResourceToolGroup,
} from './models/management-tool';
import { ResourceContextBuilder } from './resource-context-snapshot';
import { ToolGroupResolver } from './tool-group-resolver';

function isNavigationEnd(event: unknown): event is NavigationEnd {
  return event instanceof NavigationEnd;
}

@Injectable({
  providedIn: 'root',
})
export class ManagementToolContextService {
  private _router = inject(Router);
  private _registry = inject(ManagementToolRegistryService);
  private _injector = inject(Injector);
  private _management = inject(ManagementService);
  private _sidebar = inject(ManagementToolSidebarService);

  private _contextSnapshot$: Observable<IResourceContext[]> =
    this._router.events.pipe(
      filter(isNavigationEnd),
      startWith(undefined),
      map(() =>
        ResourceContextBuilder.fromRoute(
          this._router.routerState.snapshot.root,
          this._registry.getResources(),
          (resource, value, key) => this._buildContext(resource, value, key)
        )
      ),
      switchMap((routeContexts) =>
        from(
          ResourceContextBuilder.resolveRelated(
            routeContexts,
            this._registry.getResources(),
            (resource, value, key) => this._buildContext(resource, value, key)
          )
        )
      ),
      shareReplayCold()
    );

  contexts$: Observable<IResourceContext[]> = this._contextSnapshot$.pipe(
    startWith([])
  );

  resourceGroups$: Observable<IResourceToolGroup[]> = combineLatest([
    this._contextSnapshot$,
    this._management.user$,
    this._sidebar.showDevTools$,
  ]).pipe(
    switchMap(([contexts]) =>
      from(ToolGroupResolver.resolve(contexts, this._registry.getTools()))
    ),
    startWith([]),
    shareReplayCold()
  );

  private _buildContext(
    resource: IManagementResource,
    value: unknown,
    routeDataKey: string
  ): IResourceContext {
    const value$ = isWithRef<object>(value)
      ? Firestore.doc$(value.ref).pipe(shareReplayCold())
      : of(value);
    const label$ = value$.pipe(
      map((v) => resource.resolveLabel(v, this._injector))
    );
    return { resource, routeDataKey, label$, value$ };
  }
}
